import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MessageResponse, Response } from '@shared/models/response.model';
import { UserModel } from '@shared/models/user.model';
import { AuthService } from '@core/services/auth/auth.service';
import { AUTH_REQUIRED } from '@core/tokens/auth-required.token';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userInfo$: BehaviorSubject<UserModel | null> =
    new BehaviorSubject<UserModel | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {}

  public getUserInfo(): Observable<Response<UserModel>> {
    const token = this.authService.getAuthData().access_token;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http
      .get<Response<UserModel>>(`${environment.apiUrl}/user/person`, {
        headers,
        context: new HttpContext().set(AUTH_REQUIRED, true),
      })
      .pipe(tap(() => this.authService.isAuthenticated$.next(true)));
  }

  public updateContact(
    languages: string[],
    timezone: string,
    region: string
  ): Observable<MessageResponse<UserModel>> {
    return this.http.post<MessageResponse<UserModel>>(
      `${environment.apiUrl}/contact`,
      {
        languages,
        timezone,
        region,
      },
      { context: new HttpContext().set(AUTH_REQUIRED, true) }
    );
  }

  public changePublicUserIdentificators(
    nickname: string,
    public_id?: string
  ): Observable<MessageResponse<UserModel>> {
    return this.http.put<MessageResponse<UserModel>>(
      `${environment.apiUrl}/user/identificators`,
      null,
      {
        params: {
          nickname,
          ...(public_id ? { public_id } : {}),
        },
        context: new HttpContext().set(AUTH_REQUIRED, true),
      }
    );
  }

  public changeSettings(settings: string[]): Observable<Response<UserModel>> {
    return this.http.post<any>(
      `${environment.apiUrl}/setting/user`,
      {
        settings,
      },
      { context: new HttpContext().set(AUTH_REQUIRED, true) }
    );
  }

  public setUserSettingByKey(
    settingKey: string,
    value: 'on' | 'off'
  ): Observable<Response<UserModel>> {
    return this.http.post<any>(
      `${environment.apiUrl}/setting/user/by_key`,
      {
        settingKey,
        value,
      },
      { context: new HttpContext().set(AUTH_REQUIRED, true) }
    );
  }

  public get isDiscordVerified(): boolean {
    return this.userInfo$.value!.discord_auth.discord_username != '';
  }

  public get isExboVerified(): boolean {
    return this.userInfo$.value!.exbo_auth.exbo_login != '';
  }
}
